import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { BookingStatisticsResponse } from "../../../types";

interface BookingsChartProps {
  stats: BookingStatisticsResponse;
}

export function BookingsChart({ stats }: BookingsChartProps) {
  const { t } = useTranslation();

  const data = [
    {
      name: t("admin.pending", "Pending"),
      count: stats.pending,
      color: "#f59e0b", // Amber/Yellow
    },
    {
      name: t("admin.confirmed", "Confirmed"),
      count: stats.confirmed,
      color: "#16a34a", // Green
    },
    {
      name: t("admin.cancelled", "Cancelled"),
      count: stats.cancelled,
      color: "#dc2626", // Red
    },
  ];

  // Custom label renderer for showing "count (percentage%)" above the bars
  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, value } = props;
    if (value === undefined || value === null) return null;
    const percentage =
      stats.total > 0 ? Math.round((value / stats.total) * 100) : 0;
    return (
      <text
        x={x + width / 2}
        y={y - 8}
        fill="#4b5563"
        fontSize={12}
        fontWeight={600}
        textAnchor="middle"
      >
        {`${value} (${percentage}%)`}
      </text>
    );
  };

  // Custom tooltip to show both count and percentage on hover
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const percentage =
        stats.total > 0 ? Math.round((dataPoint.count / stats.total) * 100) : 0;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e9ecef",
            borderRadius: "10px",
            padding: "10px 14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <p
            style={{
              fontWeight: 600,
              margin: 0,
              color: "#1f2937",
              fontSize: "13px",
            }}
          >
            {dataPoint.name}
          </p>
          <p
            style={{
              margin: "4px 0 0 0",
              color: dataPoint.color,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {`${dataPoint.count} ${t("admin.bookings", "bookings")} (${percentage}%)`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="admin-chart-section">
      <div className="admin-chart-header">
        <div>
          <h3 className="admin-chart-title">
            {t("admin.bookingStatusTitle", "Booking status")}
          </h3>
          <p className="admin-chart-subtitle">
            {t(
              "admin.bookingStatusDesc",
              "Distribution across all {{count}} bookings",
              { count: stats.total },
            )}
          </p>
        </div>
        <div
          className="admin-badge"
          style={{
            background: "rgba(15, 118, 110, 0.06)",
            color: "#0f766e",
            borderColor: "rgba(15, 118, 110, 0.12)",
          }}
        >
          {t("admin.bookingsStat")}: &nbsp;<strong>{stats.total}</strong>
        </div>
      </div>

      <div className="admin-chart-container" style={{ marginTop: "20px" }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: "#8c8c9a" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#8c8c9a" }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              domain={[
                0,
                (dataMax: number) => Math.max(14, Math.ceil(dataMax * 1.3)),
              ]}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.02)", radius: 8 }}
            />
            <Bar
              dataKey="count"
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
